/**
 * UploadSuccessModal
 * 
 * Shows after successful upload with:
 * - Success message
 * - Single asset: Content ID with copy button
 * - Multiple assets: List of Content IDs with individual copy + copy all
 * - Next steps guidance
 * - Link to MatchFlow
 */

import React, { useState } from 'react';

export default function UploadSuccessModal({ 
  isOpen, 
  onClose, 
  assets = [], // Array of { contentId, fileName }
  destination,
  isBatchMode = false, // true for LED (shared ID), false for Screens (individual IDs)
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyAll = async () => {
    try {
      const allIds = assets.map(a => a.contentId).join('\n');
      await navigator.clipboard.writeText(allIds);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGoToMatchFlow = () => {
    onClose();
    window.location.href = '/matchflow';
  };

  const isSingleAsset = assets.length === 1;
  const firstAsset = assets[0] || {};

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Close button */}
        <button 
          onClick={onClose}
          style={styles.closeBtn}
          title="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Success icon */}
        <div style={styles.iconWrapper}>
          <div style={styles.iconCircle}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 style={styles.title}>
          {isSingleAsset ? 'Upload Successful!' : `${assets.length} Files Uploaded!`}
        </h2>
        
        {/* Subtitle */}
        <p style={styles.subtitle}>
          {isSingleAsset 
            ? 'Your content has been uploaded and is ready for scheduling.'
            : 'Your content has been uploaded. Each file has a unique Content ID for scheduling.'
          }
        </p>

        {/* Destination badge */}
        {destination && (
          <div style={styles.destinationBadge}>
            📍 {destination}
          </div>
        )}

        {/* Single asset view */}
        {isSingleAsset ? (
          <>
            {/* Content details card */}
            <div style={styles.detailsCard}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>File Name</span>
                <span style={styles.detailValue}>{firstAsset.fileName || 'Uploaded Content'}</span>
              </div>
            </div>

            {/* Lumen8 ID section */}
            <div style={styles.contentIdSection}>
              <div style={styles.contentIdLabel}>Your Lumen8 ID</div>
              <div style={styles.contentIdBox}>
                <span style={styles.contentIdText}>{firstAsset.contentId}</span>
                <button 
                  onClick={() => handleCopy(firstAsset.contentId)}
                  style={styles.copyBtn}
                  title="Copy to clipboard"
                >
                  {copiedId === firstAsset.contentId ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Multiple assets view */
          <>
            <div style={styles.contentIdSection}>
              <div style={styles.contentIdLabel}>Your Lumen8 IDs</div>
              
              {/* Scrollable list of assets */}
              <div style={styles.assetList}>
                {assets.map((asset, index) => (
                  <div key={asset.contentId || index} style={styles.assetRow}>
                    <div style={styles.assetInfo}>
                      <span style={styles.assetId}>{asset.contentId}</span>
                      <span style={styles.assetFileName}>{asset.fileName}</span>
                    </div>
                    <button 
                      onClick={() => handleCopy(asset.contentId)}
                      style={styles.copyBtnSmall}
                      title="Copy ID"
                    >
                      {copiedId === asset.contentId ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Copy all button */}
              <button 
                onClick={handleCopyAll}
                style={styles.copyAllBtn}
              >
                {copiedAll ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    All IDs Copied!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy All Lumen8 IDs
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Next steps */}
        <div style={styles.nextSteps}>
          <div style={styles.nextStepsTitle}>📋 What's Next?</div>
          <ol style={styles.stepsList}>
            <li style={styles.stepItem}>
              Go to <strong>MatchFlow Builder</strong> to schedule your content
            </li>
            <li style={styles.stepItem}>
              Find or create the row where you want this content
            </li>
            <li style={styles.stepItem}>
              Search for {isSingleAsset ? (
                <strong>{firstAsset.contentId}</strong>
              ) : (
                <span>each <strong>Lumen8 ID</strong></span>
              )} in the asset picker
            </li>
          </ol>
        </div>

        {/* Action buttons */}
        <div style={styles.actions}>
          <button 
            onClick={onClose}
            style={styles.secondaryBtn}
          >
            Upload More
          </button>
          <button 
            onClick={handleGoToMatchFlow}
            style={styles.primaryBtn}
          >
            Go to MatchFlow
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
    backdropFilter: 'blur(4px)',
  },
  
  modal: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '32px 28px',
    maxWidth: 500,
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    position: 'relative',
  },

  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#6b7280',
    transition: 'all 0.15s ease',
  },
  
  iconWrapper: {
    marginBottom: 20,
  },
  
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(34, 197, 94, 0.3)',
  },
  
  title: {
    margin: '0 0 8px 0',
    fontSize: 24,
    fontWeight: 800,
    color: '#111827',
  },
  
  subtitle: {
    margin: '0 0 16px 0',
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 1.5,
  },

  destinationBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    background: '#f3f4f6',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 20,
  },
  
  detailsCard: {
    background: '#f9fafb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    textAlign: 'left',
  },
  
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  },
  
  detailLabel: {
    fontSize: 13,
    color: '#6b7280',
  },
  
  detailValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
    maxWidth: '65%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  
  contentIdSection: {
    marginBottom: 20,
  },
  
  contentIdLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  
  contentIdBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'linear-gradient(135deg, #f0fdf9 0%, #ecfdf5 100%)',
    border: '2px solid #2fd6b5',
    borderRadius: 12,
    padding: '14px 16px',
  },
  
  contentIdText: {
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
    fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
    letterSpacing: 0.5,
  },
  
  copyBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },

  // Multiple assets styles
  assetList: {
    background: '#f9fafb',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    maxHeight: 200,
    overflowY: 'auto',
    marginBottom: 12,
  },

  assetRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'left',
  },

  assetInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: 12,
  },

  assetId: {
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
  },

  assetFileName: {
    display: 'block',
    fontSize: 12,
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: 2,
  },

  copyBtnSmall: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#6b7280',
    flexShrink: 0,
  },

  copyAllBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '10px 16px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    cursor: 'pointer',
  },
  
  nextSteps: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    textAlign: 'left',
  },
  
  nextStepsTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#92400e',
    marginBottom: 12,
  },
  
  stepsList: {
    margin: 0,
    paddingLeft: 20,
  },
  
  stepItem: {
    fontSize: 13,
    color: '#78350f',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  
  actions: {
    display: 'flex',
    gap: 12,
  },
  
  secondaryBtn: {
    flex: 1,
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 700,
    color: '#374151',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
  },
  
  primaryBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
    background: 'linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
  },
};
